<template>
  <div :id="`${source}_image_attachment_display`" class="max-w-[300px] w-full">
    <div
      class="relative rounded-xl overflow-hidden cursor-pointer bg_4 min-h-[100px] group"
      :class="{ 'rounded-t-xl rounded-b-none': props.caption }"
      @click="showPreview"
    >
      <img
        class="w-full h-auto max-h-[300px] object-cover block rounded-xl"
        :class="{ 'rounded-t-xl rounded-b-none': props.caption }"
        :src="attachment.url || ''"
        :alt="`${attachment?.file_name?.toLowerCase()}_preview`"
        @load="onImageLoad"
        @error="onImageError"
      />
      <div v-if="imageLoading" class="absolute inset-0 flex items-center justify-center bg_8 rounded-xl">
        <CustomLoader :size="24" />
      </div>
      <div v-if="imageError" class="absolute inset-0 flex items-center justify-center bg_8 rounded-xl">
        <ThemeImage
          :icon="imageNames.IMAGE_GREY"
          class="size-8 opacity-50"
          alt="image_error"
        />
      </div>
      <div class="media_overlay absolute inset-0 flex items-end justify-end p-2 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity">
        <div class="flex gap-1.5">
          <div
            class="flex items-center justify-center size-8 rounded-full cursor-pointer bg-black/50 backdrop-blur-sm transition-all hover:bg-black/70 hover:scale-110"
            @click.stop="downloadImage"
            title="Download"
          >
            <ThemeImage
              :icon="imageNames.DOWNLOAD_GREY"
              class="size-4"
              alt="download"
            />
          </div>
          <div
            class="flex items-center justify-center size-8 rounded-full cursor-pointer bg-black/50 backdrop-blur-sm transition-all hover:bg-black/70 hover:scale-110"
            @click.stop="showPreview"
            title="Expand"
          >
            <ThemeImage
              :icon="imageNames.EXPAND_GREY"
              class="size-4"
              alt="expand"
            />
          </div>
        </div>
      </div>
      <div v-if="props.caption" class="bg_8 py-2 px-3 text-sm leading-[1.4] text_color rounded-b-xl break-words">
        {{ props.caption }}
      </div>
    </div>

    <ImagePreview
      :source="source"
      :visible="previewVisible"
      :url="attachment.url"
      @update:visible="previewVisible = $event"
    />
  </div>
</template>

<script setup>
import { ref } from "vue";
import ImagePreview from "@/src/components/preview_components/ImagePreview.vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import CustomLoader from "@/src/components/CustomLoader.vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import { downloadAttachment } from "@/src/utils/chatUtils.js";

const props = defineProps({
  source: { type: String, required: true },
  attachment: { type: Object, required: true },
  caption: { type: String, default: "" },
});

const previewVisible = ref(false);
const imageLoading = ref(true);
const imageError = ref(false);

const showPreview = () => {
  previewVisible.value = true;
};

const onImageLoad = () => {
  imageLoading.value = false;
  imageError.value = false;
};

const onImageError = () => {
  imageLoading.value = false;
  imageError.value = true;
};

const downloadImage = async () => {
  if (!props.attachment?.url) return;

  const isChatSource = props.source === 'chat_message' || props.source.includes('chat');

  if (isChatSource) {
    await downloadAttachment(props.attachment, (errorMsg) => {
      console.error("Error downloading image:", errorMsg);
    });
    return;
  }

  try {
    const response = await fetch(props.attachment.url);
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = props.attachment.file_name || "image";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error("Error downloading image:", error);
  }
};
</script>

<style scoped>
.media_overlay {
  background: linear-gradient(to top, rgba(0, 0, 0, 0.4) 0%, transparent 40%);
}
</style>
